import styles from './styles.module.css'
import './index.scss'

import { ComponentType, lazy, MutableRefObject, Suspense, useEffect, useMemo, useRef } from 'react'
import { useJewelViewer } from './viewer/hooks/useJewelViewer'
import LoaderComponent from '../../trillion-common/src/Components/Loaders/loader-component'
import { JewelryTypeName } from '../../trillion-common/src/typings/jewelry-type'
import PointOfViewButtonsList from './viewer/components/pointOfViewButtonsList'
import {
  DEFAULT_CAMERA_POSITION,
  DEFAULT_VIEWER_BACKGROUND,
  DEFAULT_VIEWER_ID,
  DEFAULT_VIEWER_TYPE,
} from './viewerSettings'
import { useAppStore } from './shared/store/store'
import { useViewerConfig } from './viewer/hooks/useViewerConfig'
import { OnViewerConfigSaved } from './viewer/types/on-viewer-config-saved'
import { OnWidgetConfigSaved } from './viewer/types/on-widget-config-saved'
import { OnEnvironmentUpload } from './viewer/types/on-environment-upload'
import { TrillionBackend } from '../../trillion-common/src/API/trillion-backend'
import { ViewerRenderer } from './viewer/components/ViewerRenderer/ViewerRenderer'
import { SelectColor } from './viewer/components/SelectColor'
import { SelectCut } from './viewer/components/SelectCut'
import { SelectSize } from './viewer/components/SelectSize'
import { MaterialColor } from '../../trillion-common/src/objects/material-color.object'
import { DiamondCut } from '../../trillion-common/src/objects/diamond-cut.object'
import { StoneSize } from '../../trillion-common/src/objects/stone-size.object'
import { Engraving } from './viewer/components/Engraving/Engraving'
import { LetterConfigurator } from './viewer/components/LetterConfigurator/LetterConfigurator'
import { LoaderTheme } from './viewer/types/loader-theme'
import { ModelButtonToggle } from './viewer/components/ModelButtonToggle/ModelButtonToggle'
import { useWidgetConfig } from './viewer/hooks/useWidgetConfig'
import { useGoogleTagManager } from './viewer/hooks/useGoogleTagManager'
import { useMonitoring } from './viewer/hooks/useMonitoring'
import { ExposeStateAndModel } from './viewer/utils/ExposeState'
import { TryonBtn } from './viewer/components/TryonBtn/TryonBtn'

export interface ICustomLoaderComponent {
  progress?: number
  showAnimation?: boolean
}

export type CustomLoaderType =
  | 'bulgari'
  | 'dior'
  | 'channel'
  | 'camille'
  | 'claseAzul'
  | 'kooheji'
  | 'hermes'
  | 'lazurde'
  | 'apm'
  | 'kismet'
  | 'mejuri'
  | 'swarovski'
  | 'doji'
  | 'colibri'
  | 'parure'
  | 'oscar'
  | 'excellence'

export interface TrillionViewerProps {
  jewelryId?: string | null
  jewelryType?: JewelryTypeName | null
  isGUIEnabled?: boolean
  isPerformanceEnabled?: boolean
  autoRotateSpeed?: number | undefined
  reducePixelRatio?: boolean
  rotateObject?: boolean
  rotateSpeed?: number | undefined
  background?: string | undefined
  onViewerConfigSaved?: OnViewerConfigSaved
  onWidgetConfigSaved?: OnWidgetConfigSaved
  onEnvironmentUpload?: OnEnvironmentUpload
  customBackendUrl?: string | null
  zoomMax?: number | undefined
  zoomMin?: number | undefined
  startSize?: number | undefined
  darkPointsOfView?: boolean | undefined
  loaderTheme?: LoaderTheme | null
  posteffectsConfigJson?: string | undefined
  customLoader?: CustomLoaderType
  serviceActivationKey?: string | null
  disableShadowRoot?: boolean
  cancelMirroring?: boolean
  tryon?: boolean
  useExternalConfigurator?: boolean
}

export const TrillionViewer = ({
  jewelryId,
  jewelryType,
  isGUIEnabled,
  isPerformanceEnabled,
  autoRotateSpeed,
  reducePixelRatio,
  rotateObject,
  rotateSpeed,
  background,
  onViewerConfigSaved,
  onWidgetConfigSaved,
  onEnvironmentUpload,
  customBackendUrl = null,
  zoomMin,
  zoomMax,
  startSize,
  darkPointsOfView,
  loaderTheme,
  posteffectsConfigJson,
  customLoader,
  serviceActivationKey = null,
  cancelMirroring,
  tryon,
  useExternalConfigurator = false,
}: TrillionViewerProps): JSX.Element => {
  const isLoading = useAppStore((state) => state.isLoading)
  const cameraController = useAppStore((state) => state.cameraController)

  const setIsGUIEnabled = useAppStore((state) => state.setIsGUIEnabled)
  const setIsPerformanceEnabled = useAppStore((state) => state.setIsPerformanceEnabled)
  const setAutoRotateSpeed = useAppStore((state) => state.setAutoRotateSpeed)
  const setIsFreeMoveCameraEnabled = useAppStore((state) => state.setIsFreeMoveCameraModeEnabled)
  const setReducePixelRatio = useAppStore((state) => state.setReducePixelRatio)
  const setRotateObject = useAppStore((state) => state.setRotateObject)
  const setRotateSpeed = useAppStore((state) => state.setRotateSpeed)
  const setBackground = useAppStore((state) => state.setBackground)
  const setPointsOfView = useAppStore((state) => state.setPointsOfView)
  const setCurrentPointOfViewIndx = useAppStore((state) => state.setCurrentPointOfViewIndx)
  const setOnViewerConfigSaved = useAppStore((state) => state.setOnViewerConfigSaved)
  const setOnWidgetConfigSaved = useAppStore((state) => state.setOnWidgetConfigSaved)
  const setOnEnvironmentUpload = useAppStore((state) => state.setOnEnvironmentUpload)
  const setZoomMax = useAppStore((state) => state.setZoomMax)
  const setZoomMin = useAppStore((state) => state.setZoomMin)
  const setStartSize = useAppStore((state) => state.setStartSize)
  const setDarkPointsOfView = useAppStore((state) => state.setDarkPointsOfView)
  const setLoaderTheme = useAppStore((state) => state.setLoaderTheme)
  const setPosteffectsConfigJson = useAppStore((state) => state.setPosteffectsConfigJson)
  const setTryon = useAppStore((state) => state.setTryon)

  const rootContainer: MutableRefObject<HTMLElement | undefined> = useRef()

  useEffect(() => {
    TrillionBackend.changeBaseURL(customBackendUrl)
  }, [customBackendUrl])

  useEffect(() => {
    TrillionBackend.setServiceActivationKey(serviceActivationKey)
  }, [serviceActivationKey])

  const jewelryIdToLoad = jewelryId ?? DEFAULT_VIEWER_ID

  const viewerConfig = useViewerConfig(jewelryIdToLoad, jewelryType ?? DEFAULT_VIEWER_TYPE)
  const viewerCameraRotation = viewerConfig?.cameraRotation

  useMonitoring(jewelryIdToLoad)

  if (isGUIEnabled) {
    useWidgetConfig(jewelryIdToLoad, jewelryType ?? DEFAULT_VIEWER_TYPE)
  }

  useEffect(() => {
    setOnViewerConfigSaved(onViewerConfigSaved)
  }, [onViewerConfigSaved])

  useEffect(() => {
    setOnWidgetConfigSaved(onWidgetConfigSaved)
  }, [onWidgetConfigSaved])

  useEffect(() => {
    setOnEnvironmentUpload(onEnvironmentUpload)
  }, [onEnvironmentUpload])

  useEffect(() => {
    const pointsOfView = viewerConfig?.pointsOfView ?? []
    setPointsOfView(pointsOfView)
    setCurrentPointOfViewIndx(pointsOfView.length > 0 ? 0 : -1)
  }, [cameraController, viewerConfig])

  useEffect(() => {
    setIsGUIEnabled(isGUIEnabled ?? false)
  }, [isGUIEnabled])

  useEffect(() => {
    setIsPerformanceEnabled(isPerformanceEnabled ?? false)
  }, [isPerformanceEnabled])

  useEffect(() => {
    setZoomMax(zoomMax)
  }, [zoomMax])

  useEffect(() => {
    setZoomMin(zoomMin)
  }, [zoomMin])

  useEffect(() => {
    setStartSize(startSize)
  }, [startSize])

  useEffect(() => {
    setDarkPointsOfView(darkPointsOfView ?? false)
  }, [darkPointsOfView])

  useEffect(() => {
    setAutoRotateSpeed(autoRotateSpeed || viewerCameraRotation?.autoRotateSpeed)
  }, [autoRotateSpeed, viewerCameraRotation])

  useEffect(() => {
    setIsFreeMoveCameraEnabled(viewerCameraRotation?.freeMoveCamera ?? false)
  }, [viewerCameraRotation])

  useEffect(() => {
    setRotateObject((rotateObject || viewerCameraRotation?.rotateObject) ?? false)
  }, [rotateObject, viewerCameraRotation])

  useEffect(() => {
    setReducePixelRatio(reducePixelRatio ?? false)
  }, [reducePixelRatio])

  useEffect(() => {
    setRotateSpeed(rotateSpeed)
  }, [rotateSpeed])

  useEffect(() => {
    setLoaderTheme(loaderTheme ?? 'darkOnLight')
  }, [loaderTheme])

  useEffect(() => {
    setTryon(tryon)
  }, [tryon])

  useEffect(() => {
    setPosteffectsConfigJson(posteffectsConfigJson)
  }, [posteffectsConfigJson])

  useEffect(() => {
    setBackground(background ?? DEFAULT_VIEWER_BACKGROUND)
  }, [background])

  const startCameraPosition = useMemo(() => {
    let cameraPos = DEFAULT_CAMERA_POSITION
    if (viewerConfig?.pointsOfView !== undefined && viewerConfig?.pointsOfView.length > 0) {
      cameraPos = viewerConfig?.pointsOfView[0].cameraPosition
    }
    return cameraPos
  }, [viewerConfig])

  const { model, renderer, controls, composer, stats, mixer } = useJewelViewer({
    rootContainer: rootContainer.current,
    viewerConfig,
    startCameraPosition,
    cancelMirroring,
  })

  useGoogleTagManager(jewelryIdToLoad, stats)

  const CustomLoaderComponent: ComponentType<ICustomLoaderComponent> | undefined = useMemo(() => {
    switch (customLoader) {
      case 'bulgari':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Bulgari/bulgari-loader'),
        )
      case 'dior':
        return lazy(() => import('../../trillion-common/src/Components/Loaders/Dior/dior-loader'))
      case 'channel':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Channel/channel-loader'),
        )
      case 'camille':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Camille/camille-loader'),
        )
      case 'claseAzul':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/ClaseAzul/clase-azul-loader'),
        )
      case 'kooheji':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Kooheji/kooheji-loader'),
        )
      case 'hermes':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Hermes/hermes-loader'),
        )
      case 'lazurde':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Lazurde/lazurde-loader'),
        )
      case 'apm':
        return lazy(() => import('../../trillion-common/src/Components/Loaders/Apm/apm-loader'))
      case 'kismet':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Kismet/kismet-loader'),
        )
      case 'mejuri':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Mejuri/mejuri-loader'),
        )
      case 'doji':
        return lazy(() => import('../../trillion-common/src/Components/Loaders/Doji/doji-loader'))
      case 'swarovski':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Swarovski/swarovski-loader'),
        )
      case 'colibri':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Colibri/colibri-loader'),
        )
      case 'parure':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Parure/parure-loader'),
        )
      case 'oscar':
        return lazy(() => import('../../trillion-common/src/Components/Loaders/Oscar/oscar-loader'))
      case 'excellence':
        return lazy(
          () => import('../../trillion-common/src/Components/Loaders/Excellence/excellence-loader'),
        )
    }
  }, [customLoader])

  let materialColors: MaterialColor[] = []
  if (
    viewerConfig?.materialColors &&
    (viewerConfig?.materialColors as MaterialColor[]).length > 0
  ) {
    materialColors = viewerConfig?.materialColors as MaterialColor[]
  }

  let diamondCuts: DiamondCut[] = []
  if (viewerConfig?.diamondCuts && (viewerConfig.diamondCuts as DiamondCut[]).length > 0) {
    diamondCuts = viewerConfig.diamondCuts as DiamondCut[]
  }

  let stoneSizes: StoneSize[] = []
  if (viewerConfig?.stoneSizes && (viewerConfig.stoneSizes as StoneSize[]).length > 0) {
    stoneSizes = viewerConfig.stoneSizes as StoneSize[]
  }

  const withEngraving = jewelryId === 'graver-test'

  const withLetterConfig = jewelryId === 'initial-hoops-earrings'

  const withModelButtonToggle = jewelryId === 'cap-schwarz-voxel'

  return (
    <div
      ref={rootContainer as MutableRefObject<HTMLDivElement>}
      className={styles.viewer_container}
    >
      <ExposeStateAndModel model={model} sku={jewelryIdToLoad} />
      <ViewerRenderer
        renderer={renderer}
        controls={controls}
        composer={composer}
        stats={stats}
        mixer={mixer}
        sku={jewelryIdToLoad}
      />
      {tryon && <TryonBtn jewelryId={jewelryIdToLoad} />}
      {!isLoading && viewerConfig && <PointOfViewButtonsList />}
      {!isLoading && !useExternalConfigurator && (
        <div className={styles.select_wrapper}>
          {materialColors.length > 0 && (
            <SelectColor model={model} materialColors={materialColors} />
          )}
          {diamondCuts.length > 0 && <SelectCut model={model} diamondCuts={diamondCuts} />}
          {stoneSizes.length > 0 && <SelectSize model={model} stoneSizes={stoneSizes} />}
          {withEngraving && <Engraving model={model} />}
          {withLetterConfig && <LetterConfigurator model={model} />}
          {withModelButtonToggle && <ModelButtonToggle model={model} />}
        </div>
      )}
      {isLoading && CustomLoaderComponent && (
        // Use the Suspense component to avoid infinite loop - https://github.com/facebook/react/issues/24534
        <Suspense fallback={null}>
          <CustomLoaderComponent />
        </Suspense>
      )}
      {isLoading && !CustomLoaderComponent && (
        <LoaderComponent showBackground={true} background={background} loaderTheme={loaderTheme} />
      )}
    </div>
  )
}
